import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Loader2, Mail, Pencil, Printer, Download, ListChecks } from 'lucide-react';
import { vendorInvoiceApi } from '../../services/vendorInvoiceService';
import { useVendorProfile } from '../../services/vendorProfileService';
import { useVendorSubscriptionStatus } from '../../services/subscriptionService';
import InvoiceLivePreview from '../../components/vendor/invoice/InvoiceLivePreview';
import Button from '../../components/shared/Button';
import ModalShell from '../../components/shared/ModalShell';
import { downloadInvoicePdfFromElement } from '../../utils/vendorInvoicePdf';
import { canForwardInvoiceByEmail } from '../../utils/invoiceFormat';
import { useToastContext } from '../../contexts/ToastContext';
import { VendorGuideCallout } from '../../components/vendor/guidance';

const VendorInvoiceDetail = () => {
  const { id } = useParams();
  const { data: profileResp } = useVendorProfile();
  const { success: showSuccess, error: showError } = useToastContext();
  const invoicePrintRef = useRef(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [emailTo, setEmailTo] = useState('');
  const [emailSending, setEmailSending] = useState(false);

  useEffect(() => {
    document.body.classList.add('invoice-print-root');
    return () => document.body.classList.remove('invoice-print-root');
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['vendor-invoice', id],
    queryFn: () => vendorInvoiceApi.getOne(id),
    enabled: !!id,
  });

  const { data: vendorSubPayload } = useVendorSubscriptionStatus();
  const vendorSubRaw = vendorSubPayload?.data ?? vendorSubPayload;

  const invoice = data?.data;
  const isReceipt = (invoice?.document_type || 'invoice') === 'receipt';
  const docNoun = isReceipt ? 'receipt' : 'invoice';
  const canForwardEmail = canForwardInvoiceByEmail(vendorSubRaw);
  const vp = profileResp?.data ?? {};

  const vendorLabel = vp.business_name || 'Your business';
  const vendorLogoUrl = invoice?.logo_url || vp.logo_url || vp.brand_settings?.logo_url || null;
  const vendorAddressLines = [
    vp.address,
    [vp.city, vp.state, vp.zip_code].filter(Boolean).join(', '),
    vp.country,
    vp.business_phone,
  ].filter(Boolean);

  const previewLines = useMemo(() => {
    if (!invoice) return [];
    if (invoice.line_items?.length) {
      return invoice.line_items.map((row) => ({
        description: row.description,
        quantity: Number(row.quantity),
        unit_price: Number(row.unit_price),
      }));
    }
    return [
      {
        description: (invoice.document_type || 'invoice') === 'receipt' ? 'Receipt items' : 'Invoice items',
        quantity: 1,
        unit_price: Number(invoice.subtotal ?? invoice.total_amount ?? 0),
      },
    ];
  }, [invoice]);

  const formatDisplayDate = (val) => {
    if (!val) return '';
    const s = typeof val === 'string' ? val.slice(0, 10) : val;
    try {
      return new Date(s + 'T12:00:00').toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return String(val);
    }
  };

  const bookingRef = invoice?.booking?.booking_reference || invoice?.booking?.id?.slice(0, 8);

  const canEdit =
    invoice && !['paid', 'cancelled'].includes(String(invoice.status || '').toLowerCase());

  const handlePrint = () => window.print();

  const handleDownload = async () => {
    if (!invoice || !invoicePrintRef.current) return;
    setPdfLoading(true);
    try {
      const name = `${isReceipt ? 'receipt' : 'invoice'}-${invoice.invoice_number || invoice.id || 'export'}.pdf`;
      await downloadInvoicePdfFromElement(invoicePrintRef.current, name);
    } catch (e) {
      console.error(e);
    } finally {
      setPdfLoading(false);
    }
  };

  const resolveRecipientEmail = (inv) => {
    if (!inv) return '';
    return (
      inv.client?.email ||
      inv.guest_email ||
      inv.booking?.client?.email ||
      inv.booking?.guest_email ||
      ''
    );
  };

  const resolveRecipientName = (inv) => {
    if (!inv) return '';
    return (
      inv.client?.name ||
      inv.client_name ||
      inv.guest_name ||
      inv.booking?.client?.name ||
      inv.booking?.guest_name ||
      ''
    );
  };

  const openEmailModal = () => {
    setEmailTo(resolveRecipientEmail(invoice));
    setEmailModalOpen(true);
  };

  const handleSendInvoiceEmail = async () => {
    if (!id || !emailTo.trim()) {
      showError('Missing email', 'Enter a valid recipient email address.');
      return;
    }
    setEmailSending(true);
    try {
      const res = await vendorInvoiceApi.sendEmail(id, { email: emailTo.trim() });
      if (res?.success) {
        showSuccess('Email sent', res.message || `${isReceipt ? 'Receipt' : 'Invoice'} emailed to the client.`);
        setEmailModalOpen(false);
      } else {
        showError('Could not send', res?.message || 'Try again.');
      }
    } catch (e) {
      const msg =
        e?.response?.data?.message ||
        e?.message ||
        `Could not send ${docNoun} email.`;
      showError('Could not send', msg);
    } finally {
      setEmailSending(false);
    }
  };

  if (isLoading) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-20 text-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto mb-3" />
        <p className="text-sm text-gray-500 dark:text-gray-400">Loading invoice…</p>
      </div>
    );
  }

  if (error || !invoice) {
    return (
      <div className="rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm py-16 text-center px-4">
        <p className="text-gray-700 dark:text-gray-300 mb-4">
          {isReceipt ? 'Receipt' : 'Invoice'} not found.
        </p>
        <Link to="/vendor/invoices" className="text-primary-600 dark:text-primary-400 hover:underline">
          Back to invoices
        </Link>
      </div>
    );
  }

  const client = invoice.client || {};
  const billToName = resolveRecipientName(invoice);
  const billToEmail = resolveRecipientEmail(invoice);
  const billToPhone = client.phone || invoice.guest_phone || invoice.booking?.guest_phone || '';
  const taxRateNum = invoice.tax_rate != null ? Number(invoice.tax_rate) : 0;

  return (
    <>
      <div className="inv-detail-no-print space-y-5">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
          <div className="min-w-0 flex-1">
            <Link
              to="/vendor/invoices"
              className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 mb-3"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to invoices
            </Link>
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-50">
              {isReceipt ? 'Receipt' : 'Invoice'} #{invoice.invoice_number || invoice.id}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{billToName}</p>
          </div>
          <div className="flex shrink-0 flex-col items-stretch sm:items-end">
            <div className="inline-flex max-w-full flex-wrap items-center justify-start gap-1.5 sm:justify-end">
              {canEdit && (
                <Link
                  to={`/vendor/invoices/${id}/edit`}
                  className="inline-flex h-8 shrink-0 items-center gap-1.5 !rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-2.5 text-xs font-medium text-gray-700 dark:text-gray-200 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <Pencil className="h-3.5 w-3.5 shrink-0" />
                  Edit
                </Link>
              )}
              <Button
                type="button"
                size="xs"
                variant="outline"
                onClick={handlePrint}
                icon={Printer}
                className="!h-8 shrink-0 !py-0 !rounded-xl"
              >
                Print
              </Button>
              <Button
                type="button"
                size="xs"
                onClick={handleDownload}
                icon={Download}
                disabled={pdfLoading}
                className="!h-8 shrink-0 !py-0 !rounded-xl"
              >
                {pdfLoading ? 'Preparing…' : 'Download'}
              </Button>
              {canForwardEmail ? (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={openEmailModal}
                  icon={Mail}
                  className="!h-8 shrink-0 !py-0 !rounded-xl"
                >
                  Forward to email
                </Button>
              ) : (
                <div className="inline-flex max-w-full flex-col items-end gap-0.5">
                  <button
                    type="button"
                    disabled
                    title="Available on paid plans"
                    className="inline-flex h-8 shrink-0 items-center gap-1.5 !rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 px-2.5 text-xs font-medium text-gray-400 cursor-not-allowed"
                  >
                    <Mail className="h-3.5 w-3.5 shrink-0" />
                    Forward to email
                  </button>
                  <Link
                    to="/vendor/plan"
                    className="max-w-[12rem] text-right text-[11px] leading-snug text-primary-600 dark:text-primary-400 hover:underline sm:max-w-none"
                  >
                    Upgrade plan to email invoices
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>

        <VendorGuideCallout
          title={isReceipt ? 'This receipt' : 'This invoice'}
          steps={[
            'Review line items and totals.',
            'Email the client or download a PDF.',
            'Edit while unpaid if you need changes.',
          ]}
          icon={ListChecks}
        />

        {!canEdit && (
          <p className="text-sm text-amber-800 dark:text-amber-200 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/50 rounded-xl px-4 py-2">
            This {docNoun} is {invoice.status}. Editing is disabled.
          </p>
        )}
      </div>

      <div
        id="invoice-print-area"
        ref={invoicePrintRef}
        className="max-w-3xl mx-auto mt-4"
      >
        <InvoiceLivePreview
          vendorLabel={vendorLabel}
          vendorLogoUrl={vendorLogoUrl}
          vendorAddressLines={vendorAddressLines}
          billToName={billToName}
          billToEmail={billToEmail}
          billToPhone={billToPhone}
          invoiceNumberPreview={invoice.invoice_number || invoice.id}
          invoiceDate={formatDisplayDate(invoice.invoice_date)}
          dueDate={formatDisplayDate(invoice.due_date)}
          lineItems={previewLines}
          taxRate={taxRateNum}
          subtotal={Number(invoice.subtotal ?? 0)}
          discountAmount={Number(invoice.discount_amount ?? 0)}
          discountType={invoice.discount_type}
          discountValue={invoice.discount_value != null ? Number(invoice.discount_value) : 0}
          taxAmount={Number(invoice.tax_amount ?? 0)}
          total={Number(invoice.total_amount ?? 0)}
          notes={invoice.notes}
          paymentTerms={invoice.payment_terms}
          bookingRef={bookingRef}
          documentType={invoice.document_type || 'invoice'}
          statusLabel={
            invoice.status ? invoice.status.charAt(0).toUpperCase() + invoice.status.slice(1) : ''
          }
        />
      </div>

      <ModalShell
        isOpen={emailModalOpen}
        onClose={() => !emailSending && setEmailModalOpen(false)}
        labelledBy="invoice-email-title"
        panelClassName="max-w-md inv-detail-no-print"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4 border border-gray-200 dark:border-gray-700">
          <h2 id="invoice-email-title" className="text-lg font-semibold text-gray-900 dark:text-gray-50">
            Forward {docNoun} by email
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Send a copy of this {docNoun} to your client or another address.
          </p>
          <div>
            <label htmlFor="invoice-email-to" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Recipient email
            </label>
            <input
              id="invoice-email-to"
              type="email"
              value={emailTo}
              onChange={(e) => setEmailTo(e.target.value)}
              className="w-full !rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-950 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500/40 focus:border-primary-500"
              placeholder={billToEmail || 'client@example.com'}
              autoComplete="email"
            />
            {billToEmail && emailTo === billToEmail && (
              <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
                Pre-filled from the {docNoun} client ({billToName || 'recipient'}).
              </p>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={() => setEmailModalOpen(false)}
              className="px-3 py-1.5 text-sm font-medium !rounded-xl border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
            <Button
              type="button"
              size="sm"
              onClick={handleSendInvoiceEmail}
              disabled={emailSending || !emailTo.trim()}
              icon={Mail}
              className="!rounded-xl"
            >
              {emailSending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </div>
      </ModalShell>
    </>
  );
};

export default VendorInvoiceDetail;
